import { scaleTime, scaleLinear } from "d3-scale"
import { axisBottom } from "d3-axis"
import { createSelector } from "reselect"
import moment from "moment"
import { first, last, chain } from "lodash"
import { getTotalData } from "./data"
import { xAxisTicks } from "../../helpers/x-axis-ticks"
import { getTotalItem } from "./items"

export const MARGINS = { top: 15, left: 65, right: 50, bottom: 15 }
export const getHeight = props => props.height
export const getWidth = props => props.width || 150
export const getTickFormat = props => props.tickFormat
export const getTotalTickFormat = props => props.totalTickFormat
export const getDate = props => props.date
const getStartDate = props => props.startDate
const getEndDate = props => props.endDate

export const getMinX = createSelector(
  [getStartDate, getTotalData],
  (startDate, data) => {
    if (data && data.length > 0) return first(data).date.toDate()
    return moment(startDate).toDate()
  },
)

export const getMaxX = createSelector(
  [getEndDate, getTotalData],
  (endDate, data) => {
    if (data && data.length > 0) return last(data).date.toDate()
    return moment(endDate).toDate()
  },
)

const getXTicks = createSelector(
  [getMinX, getMaxX],
  xAxisTicks,
)

export const getScaleX = createSelector(
  [getMinX, getMaxX, getWidth],
  (minX, maxX, width) => scaleTime()
    .domain([minX, maxX])
    .rangeRound([MARGINS.left, width - MARGINS.right]),
)

const getTotalMinY = createSelector(
  [getTotalData, getTotalItem],
  (data, items) => chain(data)
    .map((datum) => chain(items)
      .map(({ id }) => datum[id])
      .compact()
      .min()
      .value(),
    )
    .min()
    .value() - 0.01,
)

const getTotalMaxY = createSelector(
  [getTotalData, getTotalItem],
  (data, items) => chain(data)
    .map((datum) => chain(items)
      .map(({ id }) => datum[id])
      .compact()
      .max()
      .value(),
    )
    .max()
    .value() + 0.01,
)

export const getTotalTickNumber = createSelector(
  [getTotalMinY, getTotalMaxY],
  (minY, maxY) => {
    const variation = Math.abs(maxY - minY)
    if (variation < 10) { return variation + 1 }
    return 10
  },
)
export const getTotalScaleY = createSelector(
  [getTotalMinY, getTotalMaxY, getHeight],
  (minY, maxY, height) => {
    let domain
    // If the domain of total positions Y is smaller than 10, we pad it so the line appears in the middle-ish of the graph
    if (Math.abs(maxY - minY) > 10) {
      domain = [minY, maxY]
    } else {
      domain = [Math.max(minY - 5, 0), maxY + 5]
    }
    return scaleLinear()
      .domain(domain)
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

export const getXAxis = createSelector(
  [getHeight, getWidth, getScaleX, getXTicks],
  (height, width, scaleX, [ticks, formatter]) => ({
    fn: axisBottom()
      .scale(scaleX)
      .ticks(ticks)
      .tickSize(-height + MARGINS.top + MARGINS.bottom)
      .tickFormat(formatter),
    transform: `translate(0,${height - MARGINS.bottom})`,
    baseline: {
      x1: MARGINS.left,
      x2: width - MARGINS.right,
      y1: height - MARGINS.bottom,
      y2: height - MARGINS.bottom,
    },
  }),
)
